import React, { useState, useEffect } from 'react';
import { userAPI } from '../../services/api';
import Notification from '../../utils/notification';
import styles from './OrderList.module.css';

const OrderList = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // all, paid, pending, cancelled
  const [sortBy, setSortBy] = useState('date'); // date, amount, status

  useEffect(() => {
    fetchOrders();
  }, [filter, sortBy]);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      // 模拟API调用
      const mockOrders = [
        {
          id: 'ORD001',
          type: 'vip',
          title: 'VIP会员 - 月卡',
          description: '30天VIP会员服务',
          amount: 29.9,
          status: 'paid',
          paymentMethod: '支付宝',
          createTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
          payTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
          expireTime: new Date(Date.now() + 1000 * 60 * 60 * 24 * 29).toISOString()
        },
        {
          id: 'ORD002',
          type: 'points',
          title: '金币充值包',
          description: '1000金币充值',
          amount: 9.9,
          status: 'paid',
          paymentMethod: '微信支付',
          createTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
          payTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
          expireTime: null
        },
        {
          id: 'ORD003',
          type: 'vip',
          title: 'VIP会员 - 年卡',
          description: '365天VIP会员服务',
          amount: 199.9,
          status: 'pending',
          paymentMethod: null,
          createTime: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(),
          payTime: null,
          expireTime: null
        },
        {
          id: 'ORD004',
          type: 'points',
          title: '金币充值包',
          description: '500金币充值',
          amount: 4.9,
          status: 'cancelled',
          paymentMethod: null,
          createTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 7).toISOString(),
          payTime: null,
          expireTime: null
        },
        {
          id: 'ORD005',
          type: 'vip',
          title: 'VIP会员 - 周卡',
          description: '7天VIP会员服务',
          amount: 9.9,
          status: 'paid',
          paymentMethod: '支付宝',
          createTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
          payTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 10).toISOString(),
          expireTime: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString()
        }
      ];

      // 应用筛选
      let filteredOrders = mockOrders;
      if (filter !== 'all') {
        filteredOrders = mockOrders.filter(order => order.status === filter);
      }

      // 应用排序
      filteredOrders.sort((a, b) => {
        switch (sortBy) {
          case 'amount':
            return b.amount - a.amount;
          case 'status':
            return a.status.localeCompare(b.status);
          case 'date':
          default:
            return new Date(b.createTime) - new Date(a.createTime);
        }
      });

      setOrders(filteredOrders);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const getOrderIcon = (type) => {
    const icons = {
      vip: '👑',
      points: '💰',
      other: '📦'
    };
    return icons[type] || icons.other;
  };

  const getStatusColor = (status) => {
    const colors = {
      paid: '#52c41a',
      pending: '#faad14',
      cancelled: '#ff4d4f',
      refunded: '#1890ff'
    };
    return colors[status] || '#666';
  };

  const getStatusText = (status) => {
    const texts = {
      paid: '已支付',
      pending: '待支付',
      cancelled: '已取消',
      refunded: '已退款'
    };
    return texts[status] || status;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const isExpired = (expireTime) => {
    if (!expireTime) return false;
    return new Date(expireTime) < new Date();
  };

  const handlePayOrder = (orderId) => {
    Notification.error(`跳转到支付页面：${orderId}`);
  };

  const handleCancelOrder = (orderId) => {
    if (window.confirm('确定要取消这个订单吗？')) {
      setOrders(orders.map(order =>
        order.id === orderId
          ? { ...order, status: 'cancelled' }
          : order
      ));
    }
  };

  if (loading) {
    return (
      <div className="order-list">
        <div className="loading">
          <div className="loading-spinner"></div>
          <span>加载中...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="order-list">
      <div className="order-header">
        <h2>订单列表</h2>
        <p>查看您的所有订单记录和支付状态</p>
      </div>

      <div className="order-summary">
        <div className="summary-item">
          <span className="summary-label">总订单数</span>
          <span className="summary-value">{orders.length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">已支付</span>
          <span className="summary-value">{orders.filter(o => o.status === 'paid').length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">待支付</span>
          <span className="summary-value">{orders.filter(o => o.status === 'pending').length}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">总金额</span>
          <span className="summary-value">
            ¥{orders.filter(o => o.status === 'paid').reduce((sum, o) => sum + o.amount, 0).toFixed(2)}
          </span>
        </div>
      </div>

      <div className="order-filters">
        <div className="filter-group">
          <label>状态筛选：</label>
          <select value={filter} onChange={(e) => setFilter(e.target.value)}>
            <option value="all">全部</option>
            <option value="paid">已支付</option>
            <option value="pending">待支付</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
        <div className="filter-group">
          <label>排序方式：</label>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="date">按时间</option>
            <option value="amount">按金额</option>
            <option value="status">按状态</option>
          </select>
        </div>
      </div>

      <div className="orders-container">
        {orders.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📦</div>
            <h3>暂无订单</h3>
            <p>您还没有任何订单记录</p>
          </div>
        ) : (
          orders.map(order => (
            <div key={order.id} className="order-card">
              <div className="order-card-header">
                <div className="order-info">
                  <div className="order-icon">
                    {getOrderIcon(order.type)}
                  </div>
                  <div className="order-details">
                    <h4 className="order-title">{order.title}</h4>
                    <p className="order-description">{order.description}</p>
                    <span className="order-id">订单号：{order.id}</span>
                  </div>
                </div>
                <div className="order-amount">
                  <span className="amount">¥{order.amount}</span>
                  <span
                    className="status-badge"
                    style={{ backgroundColor: getStatusColor(order.status) }}
                  >
                    {getStatusText(order.status)}
                  </span>
                </div>
              </div>

              <div className="order-card-body">
                <div className="order-meta">
                  <div className="meta-item">
                    <span className="meta-label">创建时间：</span>
                    <span className="meta-value">{formatDate(order.createTime)}</span>
                  </div>
                  {order.payTime && (
                    <div className="meta-item">
                      <span className="meta-label">支付时间：</span>
                      <span className="meta-value">{formatDate(order.payTime)}</span>
                    </div>
                  )}
                  {order.paymentMethod && (
                    <div className="meta-item">
                      <span className="meta-label">支付方式：</span>
                      <span className="meta-value">{order.paymentMethod}</span>
                    </div>
                  )}
                  {order.expireTime && (
                    <div className="meta-item">
                      <span className="meta-label">到期时间：</span>
                      <span className={`meta-value ${isExpired(order.expireTime) ? 'expired' : ''}`}>
                        {formatDate(order.expireTime)}
                        {isExpired(order.expireTime) && ' (已过期)'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="order-actions">
                  {order.status === 'pending' && (
                    <>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => handlePayOrder(order.id)}
                      >
                        立即支付
                      </button>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => handleCancelOrder(order.id)}
                      >
                        取消订单
                      </button>
                    </>
                  )}
                  {order.status === 'paid' && (
                    <button className="btn btn-secondary btn-sm">
                      查看详情
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default OrderList;